/* eslint-disable @atlassian/perf-linting/no-quadratic-complexity */
import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';
import {
	type ReaderFragment,
	type IEnvironment,
	type ReaderSelection,
	type ReaderLinkedField,
	type ReaderScalarField,
	type ReaderArgument,
} from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';

import { toDocumentNode } from './toDocumentNode.ts';

export type ReadFragmentInteropOptions<TVariables extends Record<string, any>> = {
	id?: string;
	fragment: GraphQLTaggedNode;
	variables?: TVariables;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseReadFragmentInteropArgs = {
	id?: string;
	fragmentSpec: GraphQLTaggedNode;
	variables?: Record<string, any>;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseReadFragmentInteropResult<TData> = (params?: {
	id?: string;
	variables?: Record<string, any>;
}) => TData | null;

export type UseReadFragmentInteropHook<TData> = (
	params: UseReadFragmentInteropArgs,
) => UseReadFragmentInteropResult<TData>;

/**
 * Imperative read fragment with the same options shape as Apollo's readFragment.
 * Uses BUILD_VARIANT to call either the Relay fragment shim or Apollo client readFragment.
 */
export function readFragmentInterop<TData, TVariables extends Record<string, any> = {}>(
	options: ReadFragmentInteropOptions<TVariables>,
): TData | null {
	const { id, fragment, variables, interopApolloAggClient, __callerPackage__ } = options;
	if (!id) {
		return null;
	}

	if (isRelayEnabled(__callerPackage__)) {
		const env = window['GLOBAL_RELAY_ENV']!;
		return readFragmentShim<TData>(env, fragment, id, (variables ?? {}) as Record<string, any>);
	}

	const client = interopApolloAggClient
		? window['GLOBAL_APOLLO_AGG_CLIENT']
		: window['GLOBAL_APOLLO_CLIENT'];
	const data = client!.readFragment<TData>({
		id,
		fragment: toDocumentNode(fragment),
		variables: variables ?? {},
	});

	return data as TData;
}

function useReadFragmentInteropRelay<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	const { fragmentSpec, id: defaultId, variables: defaultVariables } = params;
	const env = useRelayEnvironment();

	return useCallback(
		(callbackParams?: { id?: string; variables?: Record<string, any> }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			return readFragmentShim(
				env,
				fragmentSpec,
				id,
				callbackParams?.variables ?? defaultVariables ?? {},
			);
		},
		[env, fragmentSpec, defaultId, defaultVariables],
	);
}

function useReadFragmentInteropApollo<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	const { fragmentSpec: fragment, id: defaultId, variables: defaultVariables } = params;

	const client = useApolloClient();

	return useCallback(
		(callbackParams?: { id?: string; variables?: Record<string, any> }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			const data = client.readFragment<TData>({
				id,
				fragment: toDocumentNode(fragment),
				variables: callbackParams?.variables ?? defaultVariables ?? {},
			});

			return data as TData;
		},
		[client, fragment, defaultId, defaultVariables],
	);
}

export function useReadFragmentInterop<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	return isRelayEnabled(params.__callerPackage__)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useReadFragmentInteropRelay(params)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useReadFragmentInteropApollo(params);
}

function apolloFragmentIdToRelayFragmentId(apolloFragmentId: string) {
	return apolloFragmentId.replace(/^\$ROOT_QUERY\./, 'client:root:');
}

/**
 * Shim function that mimics Apollo's `readFragment` using Relay
 */
function readFragmentShim<TData>(
	environment: IEnvironment,
	fragment: GraphQLTaggedNode,
	fragmentId: string,
	variables?: Record<string, any>,
): TData | null {
	// Get the store from the environment
	const store = environment.getStore();

	// Get the source from the store
	const source = store.getSource();

	// Get the record using the fragmentId
	const rootRecord = source.get(apolloFragmentIdToRelayFragmentId(fragmentId));

	if (!rootRecord) {
		return null;
	}

	// Helper to compute storage key for fields with arguments
	const getFieldKey = (
		field: ReaderLinkedField | ReaderScalarField,
		vars: Record<string, any> = {},
	): string => {
		if (!field.args || field.args.length === 0) {
			return field.name;
		}

		// Compute storage key for fields with arguments
		const args = field.args
			.map((arg: ReaderArgument) => {
				let value: string;
				if (arg.kind === 'Literal') {
					value = JSON.stringify(arg.value);
				} else if (arg.kind === 'Variable') {
					value = JSON.stringify(vars[arg.variableName]);
				} else {
					// Fallback for other argument types
					value = JSON.stringify((arg as any).value);
				}
				return `${arg.name}:${value}`;
			})
			.join(',');

		return `${field.name}(${args})`;
	};

	// Define a function to read the fields specified in the fragment
	const readFields = (recordId: string, fields: readonly ReaderSelection[]): any => {
		const record = source.get(recordId);
		if (!record) {
			return null;
		}

		const data = {} as any;
		for (const field of fields) {
			if (field.kind === 'Condition') {
				// Handle @include/@skip directives with variables
				const conditionValue = variables?.[field.condition];
				const shouldInclude = field.passingValue ? conditionValue : !conditionValue;

				if (shouldInclude) {
					// Recursively process the selections within this condition
					const conditionalData = readFields(recordId, field.selections);
					// Merge the conditional data into the result
					Object.assign(data, conditionalData);
				}
			} else if (field.kind === 'LinkedField') {
				// For linked fields, we need to get the reference and recursively read
				const fieldKey = getFieldKey(field, variables ?? {});
				const linkedRecordRef = record[fieldKey];
				if (linkedRecordRef && typeof linkedRecordRef === 'object' && '__ref' in linkedRecordRef) {
					const linkedRecordId = (linkedRecordRef as { __ref: string }).__ref;
					data[field.name] = readFields(linkedRecordId, field.selections);
				} else {
					data[field.name] = null;
				}
			} else if (field.kind === 'ScalarField') {
				const fieldKey = getFieldKey(field, variables ?? {});
				data[field.name] = record[fieldKey];
			} else if (field.kind === 'InlineFragment') {
				// Handle inline fragments (e.g., ... on SomeType)
				// For read operations, we process all inline fragment selections
				const inlineData = readFields(recordId, field.selections);
				// Merge the inline fragment data into the result
				Object.assign(data, inlineData);
			} else {
				throw new Error(`Unsupported field kind: ${field.kind}`);
			}
		}
		return data;
	};

	const readerFragment = fragment as ReaderFragment;

	const topLevelFields = readerFragment?.selections;

	return readFields(apolloFragmentIdToRelayFragmentId(fragmentId), topLevelFields ?? []);
}
